import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { changeLanguage, SUPPORTED_LANGUAGES, LanguageCode } from '../../i18n';
import { fontStyle } from '../../config/fonts';

type ThemeMode = 'light' | 'dark' | 'system';

interface OptionItemProps {
  label: string;
  isSelected: boolean;
  onPress: () => void;
  colors: ReturnType<typeof useTheme>['colors'];
}

function OptionItem({ label, isSelected, onPress, colors }: OptionItemProps) {
  return (
    <Pressable onPress={onPress}>
      <View
        style={[
          styles.optionItem,
          {
            backgroundColor: isSelected ? colors.primary : colors.surface,
            borderColor: isSelected ? colors.primary : colors.border,
            borderWidth: 1,
          },
        ]}
      >
        <Text style={[styles.optionText, fontStyle, { color: isSelected ? '#fff' : colors.text }]}>{label}</Text>
        {isSelected && (
          <Ionicons name="checkmark-circle" size={18} color="#fff" />
        )}
      </View>
    </Pressable>
  );
}

export default function SettingsScreen() {
  const { t, i18n } = useTranslation();
  const { themeMode, setThemeMode, colors } = useTheme();
  const { user, signOut } = useAuth();

  const currentLanguage = i18n.language as LanguageCode;

  const themeOptions: { value: ThemeMode; labelKey: string }[] = [
    { value: 'light', labelKey: 'settings.themeLight' },
    { value: 'dark', labelKey: 'settings.themeDark' },
    { value: 'system', labelKey: 'settings.themeSystem' },
  ];

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
  };

  const handleLanguageChange = (lang: LanguageCode) => {
    changeLanguage(lang);
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={['bottom']}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* Appearance Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, fontStyle, { color: colors.textSecondary }]}>
            {t('settings.appearance')}
          </Text>
          <View style={styles.sectionContent}>
            <Text style={[styles.label, fontStyle, { color: colors.text }]}>
              {t('settings.theme')}
            </Text>
            <View style={styles.optionsRow}>
              {themeOptions.map((option) => (
                <OptionItem
                  key={option.value}
                  label={t(option.labelKey)}
                  isSelected={themeMode === option.value}
                  onPress={() => handleThemeChange(option.value)}
                  colors={colors}
                />
              ))}
            </View>
          </View>
        </View>

        {/* Language Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, fontStyle, { color: colors.textSecondary }]}>
            {t('settings.language')}
          </Text>
          <View style={styles.sectionContent}>
            <View style={styles.optionsRow}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <OptionItem
                  key={lang.code}
                  label={lang.nativeName}
                  isSelected={currentLanguage === lang.code}
                  onPress={() => handleLanguageChange(lang.code as LanguageCode)}
                  colors={colors}
                />
              ))}
            </View>
          </View>
        </View>

        {/* Sign Out Section */}
        {user && (
          <View style={styles.signOutSection}>
            <Pressable onPress={signOut}>
              <View style={[styles.signOutButton, { backgroundColor: colors.surface, borderColor: colors.border }]}>
                <Feather name="log-out" size={18} color={colors.error} />
                <Text style={[styles.signOutText, fontStyle, { color: colors.error }]}>
                  {t('auth.signOut')}
                </Text>
              </View>
            </Pressable>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  sectionContent: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 6,
  },
  optionsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  optionItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 6,
  },
  optionText: {
    fontSize: 13,
    fontWeight: '500',
    flexShrink: 1,
  },
  signOutSection: {
    marginTop: 'auto',
    paddingTop: 16,
  },
  signOutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  signOutText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
